import React, { useCallback, useRef, useState } from 'react'

const MIN_WIDTH_PX = 490
/** The page keeps at least this much room, so the dock can never swallow it. */
const MIN_PAGE_WIDTH_PX = 480
const KEYBOARD_STEP_PX = 24
const STORAGE_KEY = 'labraChatDockWidth'

/**
 * Storage is wrapped because it throws rather than no-ops in a few real
 * situations — Safari private browsing, a blocked third-party frame — and losing
 * a width preference must never take the chat down with it.
 */
const readStoredWidth = (): number | undefined => {
  try {
    const parsed = Number(window.localStorage.getItem(STORAGE_KEY))
    return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined
  } catch {
    return undefined
  }
}

const storeWidth = (width: number): void => {
  try {
    window.localStorage.setItem(STORAGE_KEY, String(Math.round(width)))
  } catch {
    // A width that fails to persist still works for this session.
  }
}

export const clampDockWidth = (
  width: number,
  containerWidth: number
): number => {
  const max = Math.max(MIN_WIDTH_PX, containerWidth - MIN_PAGE_WIDTH_PX)
  return Math.min(Math.max(width, MIN_WIDTH_PX), max)
}

export interface ChatDockWidth {
  containerRef: React.RefObject<HTMLDivElement>
  dockRef: React.RefObject<HTMLElement>
  /** Undefined until the user picks one, so the stylesheet's default holds. */
  width: number | undefined
  isResizing: boolean
  startResize: (event: React.PointerEvent<HTMLElement>) => void
  nudge: (event: React.KeyboardEvent<HTMLElement>) => void
  reset: () => void
  minWidth: number
}

/**
 * Makes the docked chat draggable from its left edge, and remembers the width.
 *
 * The gesture writes straight onto the dock element and only commits to React
 * state on release: a `setState` per pointer move re-renders the whole thread,
 * which on a long conversation is felt.
 */
export const useChatDockWidth = (): ChatDockWidth => {
  const containerRef = useRef<HTMLDivElement>(null)
  const dockRef = useRef<HTMLElement>(null)
  const [width, setWidth] = useState<number | undefined>(readStoredWidth)
  const [isResizing, setIsResizing] = useState(false)

  const commit = useCallback((next: number) => {
    setWidth(next)
    storeWidth(next)
  }, [])

  const startResize = useCallback(
    (event: React.PointerEvent<HTMLElement>) => {
      const dock = dockRef.current
      if (!dock) return

      event.currentTarget.setPointerCapture(event.pointerId)
      setIsResizing(true)

      const container = containerRef.current?.getBoundingClientRect()
      let latest = dock.getBoundingClientRect().width

      const onMove = (move: PointerEvent) => {
        if (!container) return
        latest = clampDockWidth(container.right - move.clientX, container.width)
        dock.style.width = `${latest}px`
      }

      const onUp = () => {
        window.removeEventListener('pointermove', onMove)
        window.removeEventListener('pointerup', onUp)
        setIsResizing(false)
        commit(latest)
      }

      window.addEventListener('pointermove', onMove)
      window.addEventListener('pointerup', onUp)
    },
    [commit]
  )

  const nudge = useCallback(
    (event: React.KeyboardEvent<HTMLElement>) => {
      const direction =
        event.key === 'ArrowLeft' ? 1 : event.key === 'ArrowRight' ? -1 : 0
      if (!direction) return

      event.preventDefault()
      const current = dockRef.current?.getBoundingClientRect().width ?? 0
      const containerWidth =
        containerRef.current?.getBoundingClientRect().width ?? 0
      commit(
        clampDockWidth(current + direction * KEYBOARD_STEP_PX, containerWidth)
      )
    },
    [commit]
  )

  const reset = useCallback(() => {
    setWidth(undefined)
    try {
      window.localStorage.removeItem(STORAGE_KEY)
    } catch {
      // Nothing to clean up if storage is unavailable.
    }
  }, [])

  return {
    containerRef,
    dockRef,
    width,
    isResizing,
    startResize,
    nudge,
    reset,
    minWidth: MIN_WIDTH_PX,
  }
}
